/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { requestBaseURLGCPExtOrInt } from '@/config/serviceAPI';
import { useFeatureFlag } from '@/features/globalConfig';
import { getAxios } from '@/lib/axios';
import { createQuery, type CustomUseQueryOptions } from '@/utils/createReactQuery';
import { type QueryClient } from '@tanstack/react-query';
import { type AxiosInstance } from 'axios';
import type {
  ProductReviewsData,
  ProductReviewsOptions,
  ProductReviewsResponse,
  RatingDistribution,
} from '../interface';
import environmentConfig from '@/config/environment';

const URL = `${requestBaseURLGCPExtOrInt}/sls/b2c/product-discovery-seo-data-bs/v2/product-reviews`;

const getProductReviews = async (
  options: ProductReviewsOptions,
  axiosInstance?: AxiosInstance,
): Promise<ProductReviewsData> => {
  const {
    country,
    customerType,
    skuId,
    filterByMediaType,
    filterByStar,
    filterBykeyword,
    pageNumber,
    reviewsWithMedia,
    sortBy,
  } = options;

  const response = await getAxios(axiosInstance).get<ProductReviewsResponse>(URL, {
    params: {
      country,
      customerType,
      skuId,
      filterByMediaType,
      filterByStar: filterByStar?.join(','),
      filterBykeyword,
      pageNumber,
      reviewsWithMedia,
      sortBy,
    },
    withCredentials: !environmentConfig.DISABLE_GCP_API_CREDENTIALS,
  });
  return selector(response.data);
};

const selector = (data: ProductReviewsResponse): ProductReviewsData => {
  const ratingsDistribution: RatingDistribution[] = [5, 4, 3, 2, 1].map((star) => {
    const currentDistribution = data.ratingsDistribution?.find(
      (distribution) => distribution.star === star,
    );

    return currentDistribution ?? { star, count: 0, percentage: 0 };
  });

  return {
    ...data,
    ratingsDistribution,
  };
};

const {
  useData: useProductReviewsData,
  prefetch: prefetchProductReviewsData,
  query: productReviewsQuery,
} = createQuery('reviews', getProductReviews);

export const useProductReviews = <SelectData = ProductReviewsData>(
  options: ProductReviewsOptions &
    CustomUseQueryOptions<ProductReviewsData, SelectData> & {
      enabled?: boolean;
    },
) => {
  const productReviewsEnabled = useFeatureFlag('PRP_ENABLED') === 'true';

  return useProductReviewsData({
    ...options,
    enabled: productReviewsEnabled && options.enabled,
  });
};

export const prefetchProductReviews = (
  options: ProductReviewsOptions,
  queryClient: QueryClient,
  axiosInstance: AxiosInstance,
) => {
  return prefetchProductReviewsData(queryClient, options, axiosInstance);
};

export const getProductReviewsFromCache = (
  options: ProductReviewsOptions,
  queryClient: QueryClient,
) => {
  return queryClient.getQueryData<ProductReviewsData>(productReviewsQuery.getFullKey(options));
};
